import http from '@/lib/http'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

/**
 * A single entry of the patient files feed (`GET /patients/:hash/files`).
 * `url` is a 300 s presigned GET — refetch the feed before opening a file.
 */
export interface PatientFile {
  id: string
  patientHash: string
  originalFilename: string | null
  mimeType: string
  sizeBytes: number | null
  /**
   * Order the file is attached to; null = broad patient-level file. The feed carries
   * the number only — link to an order page via `useOrderHashesByNumberQuery`.
   */
  orderNumber: string | null
  uploadedBy: 'patient' | 'cs'
  viewedByPrescriber: boolean
  createdAt: string
  url: string
}

type PatientFilesFeed = {
  patient_hash: string
  files: PatientFile[]
}

/** The patient's own + linked-group active files, newest first. */
export const fetchPatientFiles = async (patientHash: string): Promise<PatientFile[]> => {
  const feed = (await http.get(`/patients/${patientHash}/files`)) as PatientFilesFeed
  return feed.files
}

export const usePatientFilesQuery = (patientHash: string | undefined) => {
  return useQuery({
    queryKey: ['patient-files', patientHash],
    queryFn: () => fetchPatientFiles(patientHash!),
    enabled: !!patientHash,
    // Presigned URLs expire after 300 s — keep the feed fresh (thumbnails render
    // them directly) and always refetch on focus. Pauses in background tabs.
    refetchOnWindowFocus: true,
    refetchInterval: 4 * 60 * 1000
  })
}

export const useUploadPatientFileMutation = (patientHash: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ file, orderNumber }: { file: File; orderNumber?: string }) => {
      // Multipart form — the shared axios instance sets no JSON content-type
      // default, so the browser applies the multipart boundary itself.
      const formData = new FormData()
      formData.append('file', file)
      if (orderNumber) formData.append('orderNumber', orderNumber)
      return http.post(`/patients/${patientHash}/files`, formData) as Promise<PatientFile>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-files', patientHash] })
      enqueueSnackbar('File uploaded', { variant: 'success' })
    }
  })
}

export const useDeletePatientFileMutation = (patientHash: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) =>
      http.delete(`/patients/${patientHash}/files/${fileId}`) as Promise<{ code: string; message: string }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-files', patientHash] })
      enqueueSnackbar('File deleted', { variant: 'success' })
    }
  })
}
